import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './index.pcss';
import { connect } from 'react-redux';
import { getViewMoreEmiConfig } from '../../selectors/featureFlagsSelector';
import ToggleButton from '../../atoms/ToggleButton/ToggleButton';
import Icon from '../../atoms/Icon';
import properties from '../../../modules/ajio-util/properties';
import { getProductItemPDP, segmentDatalayerPushData } from '../../../modules/ga/GTMdatalayer';
import { useLocation } from 'react-router-dom';

const cx = classNames.bind(styles);

const mapStateToProps = (state: any) => {
  return {
    viewMoreEmiConfig: getViewMoreEmiConfig(state)
  };
};

const mapDispatchToProps = () => ({});

const connector = connect(mapStateToProps, mapDispatchToProps);

const ViewMoreEmiPlans: React.FC = (props: any) => {
  const {
    hideViewMoreEmiPlans,
    viewMoreEmiData = [],
    viewMoreEmiConfig,
    isDesktop = false,
    luxe = false,
    productData
  } = props || {};
  const [search, setSearch] = useState('');
  const [onlyNoCost, setOnlyNoCost] = useState(false);
  const [openBankId, setOpenBankId] = useState<string | null>(null);
  const [didInitOpen, setDidInitOpen] = useState(false);
  const [emiData, setEmiData] = useState(viewMoreEmiData);
  const {installmentLabel = '', interestLabel = '', totalCost = ''} = viewMoreEmiConfig
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const productId = queryParams.get('productId') || '';

  // Common template renderer for viewMoreEmiConfig strings
  const renderConfigText = (key: string, values: { [k: string]: string | number } = {}) => {
    const template = (viewMoreEmiConfig && (viewMoreEmiConfig as any)[key]) || '';
    if (!template || typeof template !== 'string') return '';
    return template.replace(/\{(\w+)\}/g, (_match, group) => {
      const value = values[group];
      return value === 0 || !!value ? String(value) : '';
    });
  };

  const formatCurrency = (value?: number | string) => {
    const num = typeof value === 'string' ? Number(value) : value || 0;
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2
      }).format(num as number);
    } catch (e) {
      return `₹${num}`;
    }
  };

  const formatRatePercent = (rate?: number | string) => {
    const n = Number(rate || 0);
    if (Number.isFinite(n)) {
      const trimmed = String(n).replace(/\.0+$/, '');
      return `${trimmed}%`;
    }
    const s = String(rate || '0').replace(/\.0+$/, '');
    return `${s}%`;
  };

  const banks = emiData && Array.isArray(emiData) ? emiData : []; // directly use API keys
  // const banks = SAMPLE_BANKS; // directly use API keys

  // Ensure first bank is opened by default once data is normalized
  useEffect(() => {
    if (!didInitOpen && banks?.length) {
      setOpenBankId(banks[0].issuerSlug);
      setDidInitOpen(true);
    }
  }, [banks, didInitOpen]);

  useEffect(() => {
    const dataFromApp = window.AppData?.getEmiPlans();
    console.log('🚀 ~ ViewMoreEmiPlans ~ dataFromApp:', dataFromApp);
    if (window.AppData?.getEmiPlans) {
      const parsed = typeof dataFromApp === 'string' ? JSON.parse(dataFromApp) : dataFromApp;
      console.log('🚀 ~ ViewMoreEmiPlans ~ parsed:', parsed);
      // const emiFromApp = JSON.parse();
      setEmiData(parsed);
      // You can now call your Node API with this data
    }

    const isNoCostAvailable = banks?.some(bank => bank?.isNoCostAvailable) || false;

    const emiDetails =
      banks?.map(bank => {
        const noCostCount =
          bank?.tenures?.filter(tenure => tenure?.type === 'NO_COST')?.length || 0;

        return {
          emi_provider: bank?.issuerSlug ?? '',
          total_plans_available: bank?.tenures?.length ?? 0,
          no_cost_emi_available: noCostCount
        };
      }) || [];

    segmentDatalayerPushData({
      event: 'EMI Plans Seen',
      screen_source: 'PDP',
      pagename: 'View EMI plan',
      no_cost_emi_available: isNoCostAvailable,
      emi_offer_count: banks?.length,
      emi_plans: {
        emi_details: emiDetails
      },
      ...(productId && {
        product_id: productId
      }),
      ...(productData && {
        ...getProductItemPDP(productData)
      })
    });
  }, []);

  useEffect(() => {
    if (onlyNoCost) {
      const isNoCostAvailable = filteredBanks?.some(bank => bank?.isNoCostAvailable) || false;

      const emiDetails =
        filteredBanks?.map(bank => {
          const noCostCount =
            bank?.tenures?.filter(tenure => tenure?.type === 'NO_COST')?.length || 0;

          return {
            emi_provider: bank?.issuerSlug ?? '',
            total_plans_available: bank?.tenures?.length ?? 0,
            no_cost_emi_available: noCostCount
          };
        }) || [];

      segmentDatalayerPushData({
        event: 'No Cost EMI Toggle',
        screen_source: 'PDP',
        pagename: 'View EMI plan',
        no_cost_emi_available: isNoCostAvailable,
        emi_offer_count: filteredBanks?.length,
        emi_plans: {
          emi_details: emiDetails
        },
        ...(productId && {
          product_id: productId
        }),
        ...(productData && {
          ...getProductItemPDP(productData)
        })
      });
    }
  }, [onlyNoCost]);

  const filteredBanks = useMemo(() => {
    const term = search.trim().toLowerCase();
    return banks?.filter((bank: any) => {
      if (!bank) return false;
      const matchesTerm = term
        ? String(bank.issuerSlug || '')
            .toLowerCase()
            .includes(term)
        : true;
      const matchesNoCost = onlyNoCost ? !!bank.isNoCostAvailable : true;
      return matchesTerm && matchesNoCost;
    });
  }, [search, onlyNoCost, banks]);

  const renderPlans = (tenures: any[] = [], issuerSlug: string = '') => {
    if (!tenures || !Array.isArray(tenures) || tenures.length === 0) {
      return <div className={cx('noPlansNote')}>No EMI breakdown available for this bank.</div>;
    }
    const processing = tenures?.find(t => t?.additionalProcessingFee)?.additionalProcessingFee;
    return (
      <div className={cx('plansTable')}>
        <div className={cx('plansHeader', { luxePlansHeader: isDesktop && luxe })}>
          <span>{installmentLabel}</span>
          <span>{interestLabel}</span>
          <span>{totalCost}</span>
        </div>
        {tenures.map((t, idx) => {
          const monthly = formatCurrency(t?.totalMonthlyAmountPayable);
          const tenure = t?.tenure || 0;
          const isNoCost =
            String(t?.type).toUpperCase() === 'NO_COST' || Number(t?.interestDiscountAmount) > 0;
          const interestText = isNoCost
            ? 'No Cost'
            : `${formatRatePercent(t?.bankInterestRate)} (₹${t?.totalInterestAmount})`;
          const showDiscountChip =
            Number(t?.instantDiscountApplied) > 0 || Number(t?.cashbackApplied > 0);
          return (
            <div key={idx} className={cx('planRowContainer')}>
              <div className={cx('planRow')}>
                <span>
                  <strong>{monthly}</strong> x {tenure}m
                </span>
                <span className={cx({ noCost: isNoCost })}>{interestText}</span>
                <span>{formatCurrency(t?.totalCost)}</span>
              </div>
              {showDiscountChip ? (
                <>
                  {t?.instantDiscountApplied > 0 && (
                    <div
                      className={cx('discountChipRow', { luxeDiscountChipRow: isDesktop && luxe })}
                    >
                      <img
                        alt="Emi-Offer-Icon"
                        className={cx('discountChipIcon')}
                        src={`${properties.getProperty('imageBaseUrl')}/static/img/${
                          isDesktop && luxe ? 'luxe-percentage.svg' : 'Emi-Offer-Icon.svg'
                        }`}
                      />
                      <span
                        className={cx('discountChipText', {
                          luxeDiscountChipText: isDesktop && luxe
                        })}
                      >
                        {renderConfigText('instantDiscountMessage', {
                          instantDiscountApplied: t?.instantDiscountApplied,
                          tenure
                        })}
                      </span>
                    </div>
                  )}
                  {t?.cashbackApplied > 0 && (
                    <div
                      className={cx('discountChipRow', { luxeDiscountChipRow: isDesktop && luxe })}
                    >
                      <img
                        alt="Emi-Offer-Icon"
                        className={cx('discountChipIcon')}
                        src={`${properties.getProperty('imageBaseUrl')}/static/img/${
                          isDesktop && luxe ? 'luxe-percentage.svg' : 'Emi-Offer-Icon.svg'
                        }`}
                      />
                      <span
                        className={cx('discountChipText', {
                          luxeDiscountChipText: isDesktop && luxe
                        })}
                      >
                        {renderConfigText('cashbackMessage', {
                          amount: t?.cashbackApplied,
                          bank: issuerSlug
                        })}
                      </span>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          );
        })}
        {processing && (
          <div className={cx('planNote')}>
            {renderConfigText('processingFeeInfo', { price: processing }) ||
              `A one-time processing fee of ₹${processing} will be charged by the bank.`}
          </div>
        )}
      </div>
    );
  };

  const handleNativeGoBack = () => {
    console.log("On click of go back", window?.webkit, window?.webkit?.messageHandlers, window?.webkit?.messageHandlers?.goEmiBack);
    console.log("On click of go back", window?.AndroidBridge, window?.AndroidBridge?.goEmiBack);
    window?.alert("On Click of Go Back");
    // iOS
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.goEmiBack) {
      window?.alert("On Click of Go Back after condition");
      window.webkit.messageHandlers.goEmiBack.postMessage('emiBack');
      return;
    }

    // Android
    if (window.AndroidBridge && window.AndroidBridge.goEmiBack) {
      window?.alert("On Click of Go Back after condition");
      window.AndroidBridge.goEmiBack('emiBack');
    }
  };

  const goBack = () => {
    if (hideViewMoreEmiPlans) {
      hideViewMoreEmiPlans();
    } else {
      handleNativeGoBack();
    }
  };

  return (
    <div
      className={cx(isDesktop ? 'container' : 'm-container', { luxeContainer: isDesktop && luxe })}
    >
      <div className={cx(isDesktop ? 'header' : 'm-header')}>
        <div className={cx('title')}>
          {!isDesktop && (
            <img
              alt="Back arrow"
              className={cx('backIcon-modal')}
              src={`${properties.getProperty('imageBaseUrl')}/static/img/back-arrow-refresh.svg`}
              onClick={goBack}
            />
          )}
          View EMI Plans
        </div>
      </div>
      <div className={cx(isDesktop && 'filterControls')}>
        <div className={cx(!isDesktop && 'searchRow')}>
          <div className={cx('searchInputWrapper')}>
            <Icon
              icon={'icon-Search'}
              iconClass={cx('searchIcon')}
              styles={{ color: '#202020', fontSize: '20px', fontWeight: 'bold' }}
              ariaHidden
            />
            <input
              type="text"
              className={cx('searchInput')}
              placeholder={renderConfigText('searchByBankNamePlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label={renderConfigText('searchByBankNamePlaceholder')}
            />
          </div>
        </div>

        <div className={cx(!isDesktop && 'toggleRow')}>
          <ToggleButton
            id="no-cost-emi-toggle"
            label={renderConfigText('noCostEmiFilterLabel')}
            isChecked={onlyNoCost}
            onChange={checked => setOnlyNoCost(checked)}
            lableCustomClass={cx('toggleLabel')}
            isForEMI
            isLuxe={isDesktop && luxe}
          />
        </div>
      </div>
      <hr className={cx(isDesktop ? 'desktop-separator' : 'separator')} />
      {filteredBanks?.length > 0 && (
        <>
          <div style={{fontWeight: '700'}} className={cx('sectionHeader')}>{renderConfigText('emiPlanLabel')}</div>
          <hr className={cx(isDesktop ? 'desktop-separator' : 'separator')} />
        </>
      )}

      <div className={cx('listWrapper')}>
        {filteredBanks?.length === 0 ? (
        <div className={cx('emptyState')}>
          {search?.trim()?.length && !onlyNoCost ? (
            <>
              <div className={cx('emptyTitle')}>
                {renderConfigText('searchedCardNotFoundMessage')}
              </div>
              <div className={cx('emptySubTitle')}>
                {renderConfigText('searchedCardNotFoundHint')}
              </div>
            </>
          ) : onlyNoCost ? (
            <>
              <div className={cx('emptyTitle')}>
                {renderConfigText('noCostEmiCardsNotFoundMessage')}
              </div>
              <div className={cx('emptySubTitle')}>{renderConfigText('noCostEmiFilterHint')}</div>
            </>
          ) : null}
        </div>
      ) : (
        <div className={cx('list')}>
          {filteredBanks?.map((bank: any) => {
            const isOpen = openBankId === bank.issuerSlug;

            const offerCount = (bank.tenures || []).reduce(
              (sum: number, t: any) => sum + (Number(t?.offerCount) || 0),
              0
            );

            const payableAmount =
              Math.min(
                ...(bank.tenures || []).map((t: any) => Number(t.totalMonthlyAmountPayable) || 0)
              ) || 0;

              const emiText = renderConfigText('emiStartingFrom', {
                totalMonthlyAmountPayable: payableAmount
              });
              const currencySymbolIndex = emiText.indexOf('₹');

              const emiStartingFromContent =
                currencySymbolIndex !== -1 ? (
                  <>
                    {emiText.substring(0, currencySymbolIndex)}
                    <strong>{emiText.substring(currencySymbolIndex)}</strong>
                  </>
                ) : (
                  <strong>{emiText}</strong>
                );

            return (
              <React.Fragment key={bank.issuerSlug}>
                <div className={cx('bankRow', { open: isOpen })}>
                  <div
                    className={cx('bankHeader')}
                    onClick={() =>
                      setOpenBankId(prev => (prev === bank.issuerSlug ? null : bank.issuerSlug))
                    }
                    role="button"
                    tabIndex={0}
                  >
                    <img className={cx('bankLogo')} src={bank.bankIcon} alt={bank.issuerSlug} />
                    <div className={cx('bankMeta')}>
                      <div className={cx('bankName')}>{bank.issuerSlug}</div>
                      <div className={cx('bankSub')}>
                        {emiStartingFromContent}
                      </div>
                      {offerCount > 0 ? (
                        <div
                          className={cx('offerCountLabel', {
                            luxeOfferCountLabel: isDesktop && luxe
                          })}
                        >
                          {offerCount} Offer{offerCount > 1 ? 's' : ''}
                        </div>
                      ) : null}
                    </div>
                    {bank?.isNoCostAvailable ? (
                      <div className={cx('noCostBadge', { luxeNoCostBadge: isDesktop && luxe })}>
                        {renderConfigText('noCostEmiLabel')}
                      </div>
                    ) : (
                      <div className={cx('fromInterest', { luxeFromInterest: isDesktop && luxe })}>
                        {(() => {
                          const rates = (bank?.tenures || [])
                            .map((t: any) => Number(t?.bankInterestRate))
                            .filter((n: number) => Number.isFinite(n));
                          const min = rates.length ? Math.min(...rates) : 0;
                          const num = formatRatePercent(min).replace('%', '');
                          return (
                            renderConfigText('startingInterestRateText', { num }) ||
                            `From @${num}% p.a`
                          );
                        })()}
                      </div>
                    )}
                    <img
                      alt={isOpen ? 'Collapse' : 'Expand'}
                      src={`${properties.getProperty('imageBaseUrl')}/static/img/${
                        isOpen ? 'up_arrow' : 'down_arrow'
                      }.svg`}
                      width={16}
                      height={16}
                    />
                  </div>
                  {isOpen && (
                    <div className={cx(isDesktop ? 'bankBody' : 'mbankBody')}>
                      {renderPlans(bank?.tenures, bank?.issuerSlug)}
                    </div>
                  )}
                </div>
                <hr className={cx(isDesktop ? 'desktop-separator' : 'separator')} />
              </React.Fragment>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
};

export default connector(ViewMoreEmiPlans);
